import React from 'react';

/**
 * Pure display component for the customer shipping/contact info section on the
 * Review Order page. Takes the shipping_info blob as-is from the order document
 * and renders an N/A fallback for each field.
 *
 * As a UX safety net, when shipping_info is absent or partially empty we
 * fall back to the logged-in user's profile fields (first_name, last_name,
 * email, country, company). Without this fallback, customers who open the
 * Review page for an in-progress order (e.g. before completing Step 3
 * Customer Info) see a wall of "N/A" which looks like a data-loss bug.
 */
export const CustomerInfoCard = ({ shippingInfo = {}, userProfile = null }) => {
  const si = shippingInfo || {};
  const u = userProfile || {};
  // Prefer shipping_info when populated, else user profile. PO number
  // lives only on shipping_info since it's order-specific.
  const first_name = si.first_name || u.first_name;
  const last_name = si.last_name || u.last_name;
  const company = si.company_name || u.company || u.company_name;
  const email = si.email || u.email;
  const phone = si.phone || u.phone;
  const address_line1 = si.address_line1 || u.address_line1;
  const address_line2 = si.address_line2;
  const city = si.city || u.city;
  const country = si.country || u.country;
  const state_province =
    si.state_province || u.state_province || u.state_region || u.province;
  const postal_code = si.postal_code || u.postal_code;
  const customer_po_number = si.customer_po_number || si.purchase_order_number;

  return (
    <div
      className="bg-white rounded-lg shadow-lg p-6"
      data-testid="customer-info-card"
    >
      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        Customer Information
      </h2>
      <div className="space-y-2 text-sm">
        {customer_po_number && (
          <Field label="Customer PO Number" value={customer_po_number} />
        )}
        <Field label="First Name" value={first_name} />
        <Field label="Last Name" value={last_name} />
        <Field label="Company" value={company} />
        <Field label="Address" value={address_line1} />
        {address_line2 && (
          <p className="text-gray-600">{address_line2}</p>
        )}
        <Field label="Telephone" value={phone} />
        <Field label="Email" value={email} />
        <Field label="Town / City" value={city} />
        <Field label="Country" value={country} />
        <Field label="Province / State" value={state_province} />
        <Field label="Postal / ZIP Code" value={postal_code} />
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <p>
    <span className="font-medium text-gray-700">{label}:</span>{' '}
    <span className="text-gray-600">{value || 'N/A'}</span>
  </p>
);
